import { useState } from 'react'

interface ValveStatusChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
  valve: {
    name: string
    status: string
    description?: string
    last_changed_by_name?: string
    last_changed_at?: string
  } | null
}

export default function ValveStatusChangeModal({ isOpen, onClose, onConfirm, valve }: ValveStatusChangeModalProps) {
  const [reason, setReason] = useState('')

  if (!isOpen || !valve) return null

  const newStatus = valve.status === 'open' ? 'closed' : 'open'
  const currentStatusPl = valve.status === 'open' ? '🟢 Otwarta' : '🔴 Zamknięta'
  const newStatusPl = newStatus === 'open' ? '🟢 Otwarta' : '🔴 Zamknięta'

  const handleConfirm = () => {
    if (!reason.trim()) return
    onConfirm(reason.trim())
    setReason('')
  }

  const handleClose = () => {
    setReason('')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={handleClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Zmiana stanu zasuwy</h2>
          <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {/* Valve name */}
          <div className="bg-gray-50 rounded-lg px-4 py-3">
            <div className="font-semibold text-gray-900 text-base">{valve.name}</div>
            {valve.description && (
              <div className="text-gray-500 text-sm mt-1">{valve.description}</div>
            )}
          </div>

          {/* Status change visualization */}
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Aktualny stan</div>
              <div className={`px-4 py-2 rounded-lg font-medium text-sm ${
                valve.status === 'open' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {currentStatusPl}
              </div>
            </div>
            <div className="text-2xl text-gray-400">→</div>
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Nowy stan</div>
              <div className={`px-4 py-2 rounded-lg font-medium text-sm ${
                newStatus === 'open' 
                  ? 'bg-green-50 text-green-700 border-2 border-green-400' 
                  : 'bg-red-50 text-red-700 border-2 border-red-400'
              }`}>
                {newStatusPl}
              </div>
            </div>
          </div>

          {/* Last changed info */}
          {valve.last_changed_by_name && (
            <div className="text-xs text-gray-400 text-center">
              Ostatnia zmiana: {valve.last_changed_by_name}
              {valve.last_changed_at && ` (${new Date(valve.last_changed_at).toLocaleString('pl-PL')})`}
            </div>
          )}

          {/* Reason field - REQUIRED */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Przyczyna zmiany stanu <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Wpisz przyczynę zmiany stanu zasuwy..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              autoFocus
            />
            {reason.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Pole wymagane - nie można zmienić stanu bez podania przyczyny</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={handleClose}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition-colors"
          >
            Anuluj
          </button>
          <button
            onClick={handleConfirm}
            disabled={!reason.trim()}
            className={`min-h-[44px] px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              newStatus === 'open'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {newStatus === 'open' ? '🟢 Otwórz zasuwę' : '🔴 Zamknij zasuwę'}
          </button>
        </div>
      </div>
    </div>
  )
}
